/**
 * ErrorState Component
 *
 * Displays error messages with optional retry functionality.
 * Used for API failures, network errors, and other error states.
 */

import React from 'react'

interface ErrorStateProps {
  /** Error message to display */
  message: string
  /** Optional title (defaults to "Something went wrong") */
  title?: string
  /** Optional retry callback */
  onRetry?: () => void
  /** Optional custom icon (defaults to warning emoji) */
  icon?: React.ReactNode
  /** Variant for different contexts */
  variant?: 'default' | 'compact' | 'inline'
  /** Optional className */
  className?: string
}

export function ErrorState({
  message,
  title = 'Something went wrong',
  onRetry,
  icon,
  variant = 'default',
  className = '',
}: ErrorStateProps) {
  const iconContent = icon ?? (
    <svg
      width={variant === 'compact' ? 24 : 32}
      height={variant === 'compact' ? 24 : 32}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  )

  if (variant === 'inline') {
    return (
      <div className={`error-state-inline ${className}`} role="alert">
        <span className="error-state-icon">{iconContent}</span>
        <span className="error-state-message">{message}</span>
        {onRetry && (
          <button onClick={onRetry} className="error-state-retry-link">
            Retry
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      className={`error-state error-state-${variant} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="error-state-icon">{iconContent}</div>
      {variant !== 'compact' && <h3 className="error-state-title">{title}</h3>}
      <p className="error-state-message">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="btn btn-secondary btn-sm error-state-retry">
          Try Again
        </button>
      )}
    </div>
  )
}

/**
 * NetworkError Component
 *
 * Specialized error state for network failures.
 */
interface NetworkErrorProps {
  onRetry?: () => void
  className?: string
}

export function NetworkError({ onRetry, className = '' }: NetworkErrorProps) {
  return (
    <ErrorState
      title="Connection Error"
      message="Unable to connect to the server. Please check your internet connection and try again."
      onRetry={onRetry}
      className={className}
      icon={
        <svg
          width={32}
          height={32}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      }
    />
  )
}

/**
 * NotFoundError Component
 *
 * Specialized error state for 404/not found errors.
 */
interface NotFoundErrorProps {
  resource?: string
  onBack?: () => void
  className?: string
}

export function NotFoundError({ resource = 'Page', onBack, className = '' }: NotFoundErrorProps) {
  return (
    <ErrorState
      title="Not Found"
      message={`The ${resource.toLowerCase()} you're looking for doesn't exist or has been removed.`}
      onRetry={onBack}
      className={className}
      icon={
        <svg
          width={32}
          height={32}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
      }
    />
  )
}

export default ErrorState
